/**
 * A phone that is not there — the gravity stream behind Preview's angle simulator.
 *
 * The point of this file is what it does NOT do: it does not compute an angle. The old simulator
 * returned a number straight to the block, so the tracker never ran, and the one thing the tracker
 * exists for — resolving which side of the 180 fold an attitude is on — could not be exercised at
 * all. Typing 250 into a box ASSERTS an overturn reading. Dragging past the fold DERIVES one, which
 * is what would catch a sign error, a late latch, or a missed wrap.
 *
 * So this produces `Vec3` gravity samples in the device body frame, and they go through
 * `createRotationTracker` exactly as a real accelerometer's would. Latch, fold, gesture
 * classification, off-axis drift and stream breaks are then real behaviour rather than labels.
 *
 * ---------------------------------------------------------------------------------------------
 * THE FOUR MOUNTINGS, AND WHAT EACH ONE ACTUALLY CHANGES
 *
 * `degrees` here is the angle of the THING BEING MEASURED — the rock's dip, the rod's elevation —
 * not the phone's own reading. That is the right quantity to put on a dial because it is the one
 * the teacher authored an answer about, and it is what makes a mis-mounting visible: the dial says
 * what the world is doing, the block says what the phone reports, and a gap between them is the
 * finding.
 *
 * Two ways to hold it, each with two ways round, which is the four a teacher can set up:
 *
 *   flat-short    on its back, SHORT edge towards you, turning about that edge
 *   flat-long     on its back, LONG edge towards you, turning about that edge
 *   upright-long  screen towards you, LONG axis along the rod
 *   upright-short screen towards you, SHORT axis along the rod
 *
 * They are not four equal options, and the asymmetry is the useful part:
 *
 *   - The two FLAT ones read IDENTICALLY. Turning a flat phone about any in-plane axis moves the
 *     screen normal by exactly the angle turned, so short edge, long edge and every diagonal
 *     between them give the same number. This is not a shortcoming to hide — it is the reason a
 *     teacher is never asked to declare the pivot, made visible: set one, set the other, watch the
 *     reading not move.
 *
 *   - The two UPRIGHT ones also read identically, but only since the tracker started measuring a
 *     sweep FROM its calibration attitude. Before that, a phone clamped with its SHORT axis along
 *     the rod read 90 while the rod was flat, because the sweep quantity is the phone's long axis
 *     against horizontal — and nothing in the gravity vector can say which way round a clamp went.
 *     Offering both is what makes that agreement visible instead of assumed.
 *
 * What cannot be separated is the posture from the pivot. A sweep needs an upright start, not
 * merely a different axis: turning a FLAT phone about its screen normal leaves gravity at
 * (0, 0, -1) for the whole rotation, so the reading never moves and there is no signal at all.
 *
 * ---------------------------------------------------------------------------------------------
 * THIS IS NOT THE PIVOT LOCK THAT WAS REJECTED
 *
 * A teacher is deliberately never asked which way a student will hold the phone: the pivot is
 * derived from the motion, and a lock cannot express the diagonal case. That decision is about the
 * block's AUTHORED DATA and is untouched here. This is a simulator standing in for the physical
 * world, and a real phone is in exactly one configuration at a time. Nothing here is written to a
 * block, and the tracker is not told which gesture it is looking at — it classifies the stream
 * itself, which means the widget exercises the classifier instead of bypassing it.
 */
import { CALIBRATION_FACE_DOWN_LIMIT_DEG } from './rotation-tracking.js';
export const ROTATION_MOUNTINGS = ['flat-short', 'flat-long', 'upright-long', 'upright-short'];
/**
 * The mounting two of the simulator's three switchers compose.
 *
 * The third — whether the phone turns about a corner or about its middle — reaches nothing here,
 * and cannot: gravity is a direction, so it sees the axis a phone turns about but never where along
 * that axis the hinge sits. Tipping on an edge and rotating about a centreline are the same signal.
 */
export function mountingFor(posture, orientation) {
    if (posture === 'flat')
        return orientation === 'portrait' ? 'flat-short' : 'flat-long';
    return orientation === 'portrait' ? 'upright-short' : 'upright-long';
}
/**
 * The dial's value, wrapped into the domain the block can actually grade.
 *
 * `evaluateRotation` grades over [0, 360] and identifies 0 with 360, so there is no such thing as an
 * authored answer of 400 — and the tracker agrees: a 400 degree sweep leaves the phone at 40, and
 * 40 is the correct reading. Wrapping rather than clamping is what makes the simulator say the same
 * thing, and it costs nothing, because 40 and 400 are the same attitude and produce the identical
 * gravity sample.
 *
 * It replaced a clamp at one turn either side, which stopped the dial dead at 720 — a bound the
 * block has no equivalent of.
 */
export function wrapTurn(degrees) {
    return ((degrees % 360) + 360) % 360;
}
const RAD = Math.PI / 180;
const cross = (a, b) => ({
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
});
const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const scale = (a, k) => ({ x: a.x * k, y: a.y * k, z: a.z * k });
const add = (a, b) => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
function unit(a) {
    const n = Math.hypot(a.x, a.y, a.z);
    return n < 1e-9 ? a : scale(a, 1 / n);
}
/** Rodrigues. `axis` must be unit length. */
function rotate(v, axis, radians) {
    const c = Math.cos(radians);
    const s = Math.sin(radians);
    return add(add(scale(v, c), scale(cross(axis, v), s)), scale(axis, dot(axis, v) * (1 - c)));
}
/**
 * Each mounting's attitude at zero and its turn axis, in device coordinates.
 *
 * FLAT is the phone on its back, gravity out through the back of the glass; the two differ only in
 * which in-plane edge it turns about, which the reading cannot tell apart. UPRIGHT has gravity in
 * the screen plane; `upright-long` sits with the long axis horizontal, which is where
 * `gravityInPlaneDegrees` reads zero, and `upright-short` is the same phone turned a quarter in its
 * clamp, which is why it starts at 90.
 */
const MOUNTINGS = {
    'flat-short': { gravity: { x: 0, y: 0, z: -1 }, pivot: { x: 1, y: 0, z: 0 } },
    'flat-long': { gravity: { x: 0, y: 0, z: -1 }, pivot: { x: 0, y: 1, z: 0 } },
    'upright-long': { gravity: { x: -1, y: 0, z: 0 }, pivot: { x: 0, y: 0, z: 1 } },
    'upright-short': { gravity: { x: 0, y: -1, z: 0 }, pivot: { x: 0, y: 0, z: 1 } },
};
/** The sample a mounting produces with the measured feature horizontal. */
export function mountingReference(mounting) {
    return MOUNTINGS[mounting].gravity;
}
/**
 * One gravity sample, for a phone in this mounting with its feature at this angle.
 *
 * Deliberately stateless: a drag recomputes from its own angle rather than accumulating, so a
 * pointer that jumps cannot leave the simulated phone somewhere the drag did not put it.
 */
export function simulateGravity(phone) {
    const { gravity, pivot } = MOUNTINGS[phone.mounting];
    // Tipping the pivot toward gravity is what takes it out of horizontal in the world — see
    // `offAxisDegrees`. At zero this is the ideal axis unchanged.
    const tip = (phone.offAxisDegrees ?? 0) * RAD;
    const axis = unit(add(scale(pivot, Math.cos(tip)), scale(unit(gravity), Math.sin(tip))));
    const turned = rotate(gravity, axis, phone.degrees * RAD);
    const noise = phone.noise ?? 0;
    if (noise <= 0)
        return turned;
    const rand = phone.random ?? Math.random;
    return add(turned, {
        x: (rand() - 0.5) * 2 * noise,
        y: (rand() - 0.5) * 2 * noise,
        z: (rand() - 0.5) * 2 * noise,
    });
}
/**
 * The samples a real stream would contain between two drag positions.
 *
 * A pointer already fires at roughly the rate an accelerometer does, so this normally returns the
 * one endpoint. It exists for the flick: unwrapping assumes the phone turns less than 180 degrees
 * between samples, and a fast drag across a wide dial can exceed that in a single event. Filling
 * the gap is what a real 60Hz stream does anyway, so this is fidelity rather than defence.
 */
export function gravityPath(from, toDegrees, maxStepDegrees = 30) {
    const span = toDegrees - from.degrees;
    const steps = Math.max(1, Math.ceil(Math.abs(span) / maxStepDegrees));
    const out = [];
    for (let i = 1; i <= steps; i++) {
        out.push(simulateGravity({ ...from, degrees: from.degrees + (span * i) / steps }));
    }
    return out;
}
/**
 * Whether calibrating at this attitude would be refused, without having to try it.
 *
 * The widget needs to say so BEFORE the press — a Calibrate button that reports a refusal after
 * the fact reads as a bug, where a dial that shows the refused arc reads as the instrument telling
 * you something true about phones. Only a FLAT mounting can reach it: a sweep turns about the
 * screen normal, so the screen's own attitude never changes.
 */
export function wouldRefuseCalibration(phone) {
    if (!phone.mounting.startsWith('flat'))
        return false;
    const from = Math.abs(((phone.degrees % 360) + 360) % 360);
    const tilt = from > 180 ? 360 - from : from;
    return tilt > CALIBRATION_FACE_DOWN_LIMIT_DEG;
}
//# sourceMappingURL=rotation-simulation.js.map